/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useCallback, useEffect, useRef } from 'preact/hooks';
import { AriaAttributesSignalExcluded } from '#utils/UNSAFE_attributeUtils';
import { BundleType } from '#resources/nls/bundle';
import { classNames } from '#utils/UNSAFE_classNames';
import { DateFieldAction } from './dateReducer';
import { EditableDateSegment } from './types';
import { isBackspaceOrDelete } from '#utils/PRIVATE_keyboardUtils';
import { isNumberOnlyString } from '#utils/PRIVATE_dayUtils';
import { segmentStyles } from './themes/SegmentStyles.css';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { useUser } from '#hooks/UNSAFE_useUser';

type AriaProps = Pick<
  AriaAttributesSignalExcluded,
  'aria-label' | 'aria-valuemax' | 'aria-valuemin' | 'aria-valuenow' | 'aria-valuetext'
>;

type Props = AriaProps & {
  /**
   * True if the segment is complete in the sense that it contains a complete valid value.
   * For instance, entering '0' and then '1-9' is considered a complete value for month and day.
   * Also true if typing any additional digit would cause the segment value to exceed the max.
   */
  isComplete?: boolean;

  /**
   * True if the segment is disabled.
   */
  isDisabled?: boolean;

  /**
   * True if the segment should be hidden.
   */
  isHidden?: boolean;

  /**
   * True if the segment is highlighted.
   */
  isHighlighted?: boolean;

  /**
   * True if the segment is invalid.
   */
  isInvalid?: boolean;

  /**
   * Specifies for accessibility purposes whether a value is required.
   */
  isRequired?: boolean;

  /**
   * True if the segment is selected.
   */
  isSelected?: boolean;

  /**
   * Placeholder displayed when the segment has no display value.
   */
  placeholder?: string;

  /**
   * The text to display for this segment.
   */
  text?: string;

  /**
   * The segment of a calendar date which is being represented, i.e. the month, day, or year.
   */
  type: EditableDateSegment;

  /**
   * Callback invoked when a change to the segment is requested via user action, such as typing
   * a new value or pressing the Backspace or Delete keys.
   */
  onChange?: (action: DateFieldAction) => void;
};

/**
 * A DateSegment renders a part of a calendar date, e.g. the day, month, or year.
 * It can be used as a spinbutton to step or spin its value.
 */
export const DateSegment = ({
  'aria-label': ariaLabel,
  'aria-valuemax': ariaValueMax,
  'aria-valuemin': ariaValueMin,
  'aria-valuenow': ariaValueNow,
  'aria-valuetext': ariaValueText,
  isComplete,
  isDisabled,
  isHidden,
  isHighlighted,
  isInvalid,
  isRequired,
  isSelected,
  onChange,
  placeholder,
  text,
  type
}: Props) => {
  const hasDisplayValue = text && text.length > 0;
  const rootClasses = classNames([
    segmentStyles.base,
    isSelected && segmentStyles.selected,
    isHighlighted && segmentStyles.highlighted
  ]);
  const placeholderClasses = classNames([
    segmentStyles.placeholder,
    isHidden || hasDisplayValue ? segmentStyles.hidden : segmentStyles.notHidden
  ]);

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const emptySegmentValueText = translations.inputDateMask_empty_segment();

  const hasLeadingZero = useRef<boolean>(false);
  const segmentRef = useRef<HTMLDivElement>(null);
  const { locale } = useUser();

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (
        isDisabled ||
        isHighlighted ||
        event.key === 'Tab' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight' ||
        event.key === 'Enter'
      ) {
        // Let the event pass through.
        return;
      }

      if (event.key === '0' && (isComplete || text === undefined)) {
        // Typing '0' is considered a leading zero if the segment is complete, in which case
        // the user is typing a new value, or if there is no current value.
        hasLeadingZero.current = true;
      } else if (isNumberOnlyString(event.key)) {
        onChange?.({
          actionType: 'updateSegment',
          data: { type, text: event.key, hasLeadingZero: hasLeadingZero.current, locale }
        });
        hasLeadingZero.current = false;
      } else if (isBackspaceOrDelete(event)) {
        onChange?.({ actionType: 'clearSegment', data: { type } });
        hasLeadingZero.current = false;
      } else if (event.key === 'ArrowUp') {
        onChange?.({ actionType: 'step', data: { direction: 'increase', locale, type } });
        hasLeadingZero.current = false;
      } else if (event.key === 'ArrowDown') {
        onChange?.({ actionType: 'step', data: { direction: 'decrease', locale, type } });
        hasLeadingZero.current = false;
      } else if (event.key === 'PageUp') {
        onChange?.({ actionType: 'page', data: { direction: 'increase', locale, type } });
        hasLeadingZero.current = false;
      } else if (event.key === 'PageDown') {
        onChange?.({ actionType: 'page', data: { direction: 'decrease', locale, type } });
        hasLeadingZero.current = false;
      } else if (event.key === 'Home') {
        onChange?.({ actionType: 'goToHome', data: { locale, type } });
        hasLeadingZero.current = false;
      } else if (event.key === 'End') {
        onChange?.({ actionType: 'goToEnd', data: { locale, type } });
        hasLeadingZero.current = false;
      }
      event.preventDefault();
      event.stopPropagation();
    },
    [isComplete, isDisabled, isHighlighted, locale, onChange, text, type]
  );

  // Used to focus the segment when isSelected is true.
  useEffect(() => {
    if (isSelected) {
      segmentRef.current?.focus();
    }
  }, [isSelected]);

  // This attribute is used to distinguish between segments when clicked (see DateField.tsx).
  const customDataAttribute = { 'data-segment': [type] };

  // If ariaValueText is undefined, substitute a string that indicates the segment is empty.
  // If ariaValueNow and ariaValueText represent the same value, omit the value text; this
  // is the same as what we do in NumberInputText.
  const valueText =
    ariaValueText === undefined
      ? emptySegmentValueText
      : ariaValueNow?.toString() === ariaValueText
      ? undefined
      : ariaValueText;

  return (
    <div
      ref={segmentRef}
      role="spinbutton"
      aria-disabled={isDisabled}
      aria-invalid={isInvalid || undefined}
      aria-label={ariaLabel}
      aria-required={isRequired}
      aria-valuemin={ariaValueMin}
      aria-valuemax={ariaValueMax}
      aria-valuenow={ariaValueNow}
      aria-valuetext={valueText}
      autocapitalize="off"
      autocorrect="off"
      class={rootClasses}
      contentEditable={!isDisabled && isSelected}
      {...customDataAttribute}
      enterkeyhint={isDisabled ? undefined : 'done'}
      inputMode={isDisabled ? 'none' : 'numeric'}
      onKeyDown={onKeyDown}
      spellcheck={false}
      tabIndex={isSelected ? 0 : -1}>
      <span aria-hidden="true" class={placeholderClasses} {...customDataAttribute}>
        {placeholder}
      </span>
      {text}
    </div>
  );
};
